
import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, Send, User, Headphones, Clock } from 'lucide-react';

const LiveChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      sender: 'support',
      name: 'Emma',
      message: 'Hi! Welcome to GoldenTicketLucky support. How can I help you today?',
      timestamp: new Date(Date.now() - 2 * 60000),
      avatar: '👩‍💼'
    }
  ]);
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const quickResponses = [
    'How do I claim my winnings?',
    'What are the draw times?',
    'How to play PowerBall?',
    'Payment methods',
    'Account verification'
  ];

  const supportTeam = [
    { name: 'Emma', status: 'online', avatar: '👩‍💼' },
    { name: 'James', status: 'online', avatar: '👨‍💼' },
    { name: 'Sarah', status: 'busy', avatar: '👩‍🔬' }
  ];

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const sendMessage = () => {
    if (!newMessage.trim()) return;

    const userMessage = {
      id: messages.length + 1,
      sender: 'user',
      name: 'You',
      message: newMessage,
      timestamp: new Date(),
      avatar: '👤'
    };

    setMessages(prev => [...prev, userMessage]);
    setNewMessage('');
    setIsTyping(true);

    // Simulate support response
    setTimeout(() => {
      const supportMessage = {
        id: messages.length + 2,
        sender: 'support',
        name: 'Emma',
        message: getAutoResponse(newMessage),
        timestamp: new Date(),
        avatar: '👩‍💼'
      };
      setMessages(prev => [...prev, supportMessage]);
      setIsTyping(false);
    }, 2000);
  };

  const sendQuickResponse = (response: string) => {
    setNewMessage(response);
    setTimeout(() => sendMessage(), 100);
  };

  const getAutoResponse = (userMessage: string) => {
    const message = userMessage.toLowerCase();
    
    if (message.includes('winning') || message.includes('claim')) {
      return "To claim your winnings, log into your account and go to 'My Winnings'. For prizes over $1,000, please contact us directly for verification.";
    }
    if (message.includes('draw') || message.includes('time')) {
      return "Our draws are held: PowerBall - Thursday 8PM, Oz Lotto - Tuesday 8PM, Saturday Lotto - Saturday 8PM (AEDT).";
    }
    if (message.includes('powerball') || message.includes('how to play')) {
      return "To play PowerBall, select 7 numbers from 1-35 plus 1 PowerBall from 1-20. Minimum bet is $5. Good luck!";
    }
    if (message.includes('payment')) {
      return "We accept Visa, Mastercard, PayPal, and bank transfer. All payments are processed securely through our encrypted system.";
    }
    
    return "Thank you for your question! I'm looking into this for you. Is there anything else I can help you with today?";
  };

  if (!isOpen) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <Button
          onClick={() => setIsOpen(true)}
          className="bg-yellow-600 hover:bg-yellow-700 rounded-full p-4 shadow-lg animate-pulse"
        >
          <MessageSquare className="w-6 h-6" />
        </Button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 w-96">
      <Card className="shadow-xl">
        <CardHeader className="bg-yellow-600 text-white rounded-t-lg">
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <Headphones className="w-5 h-5" />
              Live Support
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsOpen(false)}
              className="text-white hover:bg-yellow-700"
            >
              ×
            </Button>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
            <span>Online • Avg response: 2 min</span>
          </div>
        </CardHeader>

        <CardContent className="p-0">
          {/* Support Team */}
          <div className="p-4 border-b bg-gray-50">
            <div className="flex gap-2">
              {supportTeam.map((agent) => (
                <div key={agent.name} className="flex items-center gap-2 text-sm">
                  <span className="text-lg">{agent.avatar}</span>
                  <div>
                    <div className="font-medium">{agent.name}</div>
                    <Badge variant={agent.status === 'online' ? 'default' : 'secondary'} className={
                      agent.status === 'online' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                    }>
                      {agent.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Messages */}
          <div className="h-80 overflow-y-auto p-4 space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex gap-3 ${message.sender === 'user' ? 'flex-row-reverse' : ''}`}
              >
                <div className="text-2xl">{message.avatar}</div>
                <div className={`max-w-xs ${message.sender === 'user' ? 'text-right' : ''}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-medium text-gray-700">{message.name}</span>
                    <span className="text-xs text-gray-500">
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <div className={`p-3 rounded-lg text-sm ${
                    message.sender === 'user' 
                      ? 'bg-yellow-600 text-white' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {message.message}
                  </div>
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="flex gap-3">
                <div className="text-2xl">👩‍💼</div>
                <div className="bg-gray-100 p-3 rounded-lg">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Responses */}
          <div className="p-4 border-t bg-gray-50">
            <div className="text-sm text-gray-600 mb-2">Quick questions:</div>
            <div className="flex flex-wrap gap-2">
              {quickResponses.map((response) => (
                <Button
                  key={response}
                  variant="outline"
                  size="sm"
                  onClick={() => sendQuickResponse(response)}
                  className="text-xs"
                >
                  {response}
                </Button>
              ))}
            </div>
          </div>

          {/* Message Input */}
          <div className="p-4 border-t">
            <div className="flex gap-2">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message..."
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                className="flex-1"
              />
              <Button onClick={sendMessage} className="bg-yellow-600 hover:bg-yellow-700">
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LiveChat;
